import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheckCircle,
  faCloudUploadAlt,
  faDownload,
  faEye,
  faFileAlt,
  faHistory,
  faInfoCircle,
  faTimes,
  faTimesCircle,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import {
  getSettlementPdfRegenerationBatchSpecs,
  submitSettlementPdfRegenerationManualBatch,
  uploadSettlementPdfRegenerationBatch,
} from "../../services/settlementApi";

const TEMPLATE_HEADERS = ["Payment Ref No", "Resend Email Notifications"];
const RESEND_EMAIL_NOTIFICATIONS_DEFAULT = "no";
const ACCEPTED_EXTENSIONS = ["csv", "txt", "xlsx", "xls"];
const MANUAL_MAX_ROWS = 5000;

const normalizeHeader = (value) =>
  String(value ?? "")
    .trim()
    .replace(/^\uFEFF/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const isPrnHeader = (value) =>
  [
    "payment_ref_no",
    "payment_ref",
    "payment_reference_no",
    "payment_reference_number",
    "payment_reference",
    "paymentrefno",
    "paymentref",
    "prn",
    "prn_no",
    "prn_number",
  ].includes(normalizeHeader(value));

const isResendEmailNotificationsHeader = (value) =>
  [
    "resend_email_notifications",
    "resend_email_notification",
    "resend_email_notif",
    "resend_email",
    "resend_notifications",
    "resend_notification",
    "email_notifications",
    "email_notification",
    "send_email_notifications",
    "send_email_notification",
  ].includes(normalizeHeader(value));

const shouldResendEmailNotifications = (value) =>
  String(value ?? "").trim().toLowerCase() === "yes";

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const fileExtension = (fileName) => String(fileName || "").split(".").pop().toLowerCase();

const renderInlineMarkdown = (text) => {
  const parts = String(text ?? "").split(/(`[^`]+`)/g);
  return parts.map((part, index) => {
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code key={index} className="px-1 py-0.5 rounded bg-gray-100 text-gray-800 font-mono text-[0.92em]">
          {part.slice(1, -1)}
        </code>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const MarkdownSpecsContent = ({ content }) => {
  const lines = String(content || "").split(/\r?\n/);
  const elements = [];
  let bulletItems = [];
  let numberItems = [];

  const flushBullets = () => {
    if (bulletItems.length === 0) return;
    elements.push(
      <ul key={`ul-${elements.length}`} className="list-disc pl-6 space-y-1 text-sm text-gray-700">
        {bulletItems.map((item, index) => (
          <li key={index}>{renderInlineMarkdown(item)}</li>
        ))}
      </ul>
    );
    bulletItems = [];
  };

  const flushNumbers = () => {
    if (numberItems.length === 0) return;
    elements.push(
      <ol key={`ol-${elements.length}`} className="list-decimal pl-6 space-y-1 text-sm text-gray-700">
        {numberItems.map((item, index) => (
          <li key={index}>{renderInlineMarkdown(item)}</li>
        ))}
      </ol>
    );
    numberItems = [];
  };

  const flushLists = () => {
    flushBullets();
    flushNumbers();
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();

    if (!line) {
      flushLists();
      if (elements.length > 0) {
        elements.push(<div key={`space-${index}`} className="h-2" />);
      }
      return;
    }

    if (line.startsWith("### ")) {
      flushLists();
      elements.push(
        <h3 key={index} className="text-base font-bold text-gray-800 mt-4 mb-1">
          {renderInlineMarkdown(line.slice(4))}
        </h3>
      );
      return;
    }

    if (line.startsWith("## ")) {
      flushLists();
      elements.push(
        <h2 key={index} className="text-lg font-bold text-gray-900 mt-5 mb-2 pb-1 border-b border-gray-200">
          {renderInlineMarkdown(line.slice(3))}
        </h2>
      );
      return;
    }

    if (line.startsWith("# ")) {
      flushLists();
      elements.push(
        <h1 key={index} className="text-2xl font-bold text-gray-900 mb-3">
          {renderInlineMarkdown(line.slice(2))}
        </h1>
      );
      return;
    }

    if (line.startsWith("- ")) {
      flushNumbers();
      bulletItems.push(line.slice(2));
      return;
    }

    const numberMatch = line.match(/^\d+\.\s+(.+)$/);
    if (numberMatch) {
      flushBullets();
      numberItems.push(numberMatch[1]);
      return;
    }

    flushLists();
    elements.push(
      <p key={index} className="text-sm leading-6 text-gray-700">
        {renderInlineMarkdown(line)}
      </p>
    );
  });

  flushLists();

  return <div className="space-y-1">{elements}</div>;
};

const SettlementPdfRegenerationBatchUploadPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [specsModalOpen, setSpecsModalOpen] = useState(false);
  const [specsLoading, setSpecsLoading] = useState(false);
  const [specsContent, setSpecsContent] = useState("");
  const [specsMeta, setSpecsMeta] = useState(null);
  const [specsError, setSpecsError] = useState("");
  const [manualPrnText, setManualPrnText] = useState("");
  const [manualRows, setManualRows] = useState([]);
  const [manualPreviewOpen, setManualPreviewOpen] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const ext = fileExtension(selectedFile.name);
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      toast.error("Please select a CSV, TXT, XLSX, or XLS file.");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("File size exceeds 10 MB limit.");
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);
  };

  const extractUploadRowsFromSheet = (rows) => {
    const cleanRows = rows.filter((row) =>
      Array.isArray(row) && row.some((cell) => String(cell ?? "").trim() !== "")
    );

    if (cleanRows.length === 0) {
      throw new Error("Excel file has no readable rows.");
    }

    const firstRow = cleanRows[0] || [];
    const prnColumnIndex = firstRow.findIndex(isPrnHeader);
    const resendEmailNotificationsColumnIndex = firstRow.findIndex(isResendEmailNotificationsHeader);
    const hasRecognizedHeader = prnColumnIndex >= 0 || resendEmailNotificationsColumnIndex >= 0;
    const dataStartIndex = hasRecognizedHeader ? 1 : 0;
    const columnIndex = prnColumnIndex >= 0 ? prnColumnIndex : 0;

    const uploadRows = cleanRows
      .slice(dataStartIndex)
      .map((row) => {
        const paymentRefNo = String(row?.[columnIndex] ?? "").trim();
        const resendEmailNotifications = resendEmailNotificationsColumnIndex >= 0
          ? row?.[resendEmailNotificationsColumnIndex]
          : RESEND_EMAIL_NOTIFICATIONS_DEFAULT;

        return [
          paymentRefNo,
          shouldResendEmailNotifications(resendEmailNotifications) ? "yes" : RESEND_EMAIL_NOTIFICATIONS_DEFAULT,
        ];
      })
      .filter(([paymentRefNo]) => paymentRefNo);

    if (uploadRows.length === 0) {
      throw new Error("Excel file has no PRN values.");
    }

    return uploadRows;
  };

  const prepareFileForUpload = async (selectedFile) => {
    const ext = fileExtension(selectedFile.name);
    if (ext === "csv" || ext === "txt") {
      return {
        payloadFile: selectedFile,
        sourceOriginalFileName: "",
      };
    }

    const XLSX = await import("xlsx");
    const buffer = await selectedFile.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const firstSheetName = workbook.SheetNames?.[0];
    if (!firstSheetName) {
      throw new Error("Excel file has no worksheet.");
    }

    const worksheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: "",
      blankrows: false,
      raw: false,
    });

    const uploadRows = extractUploadRowsFromSheet(rows);
    const csvContent = [
      TEMPLATE_HEADERS.map(csvEscape).join(","),
      ...uploadRows.map((row) => row.map(csvEscape).join(",")),
    ].join("\r\n");
    const convertedName = selectedFile.name.replace(/\.(xlsx|xls)$/i, "") + "_converted.csv";

    return {
      payloadFile: new File([csvContent], convertedName, { type: "text/csv" }),
      sourceOriginalFileName: selectedFile.name,
    };
  };

  const parseManualPrnRows = () =>
    String(manualPrnText || "")
      .split(/[,\r\n]+/)
      .map((value) => value.trim())
      .filter(Boolean);

  const handleManualPreview = () => {
    const parsedPrns = parseManualPrnRows();

    if (parsedPrns.length === 0) {
      toast.error("Please enter at least one PRN.");
      return;
    }

    if (parsedPrns.length > MANUAL_MAX_ROWS) {
      toast.error(`Manual PRN entry is limited to ${MANUAL_MAX_ROWS.toLocaleString()} rows.`);
      return;
    }

    setManualRows(
      parsedPrns.map((paymentRefNo, index) => ({
        id: `${Date.now()}-${index}-${paymentRefNo}`,
        payment_ref_no: paymentRefNo,
        resend_email_notifications: false,
      }))
    );
    setUploadResult(null);
    setManualPreviewOpen(true);
  };

  const handleManualResendToggle = (index, checked) => {
    setManualRows((rows) =>
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, resend_email_notifications: checked } : row
      )
    );
  };

  const handleManualSelectAllResend = (checked) => {
    setManualRows((rows) =>
      rows.map((row) => ({ ...row, resend_email_notifications: checked }))
    );
  };

  const handleManualRemoveRow = (index) => {
    setManualRows((rows) => rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const handleManualSubmit = async () => {
    if (manualRows.length === 0) {
      toast.error("No manual PRN rows to submit.");
      return;
    }

    setManualSubmitting(true);
    try {
      const payload = {
        rows: manualRows.map((row) => ({
          payment_ref_no: row.payment_ref_no,
          resend_email_notifications: Boolean(row.resend_email_notifications),
        })),
      };

      const response = await submitSettlementPdfRegenerationManualBatch(payload);
      const result = response.data;
      setUploadResult({
        success: true,
        source: "manual",
        uploadId: result.upload_id,
        fileName: result.file_name,
        fileSize: result.file_size,
        rowCount: result.row_count,
        message: result.message,
      });
      toast.success("Manual PRN batch submitted. Processing started in background.");
      setManualPreviewOpen(false);
      setManualPrnText("");
      setManualRows([]);
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message || "Manual batch submission failed.";
      setUploadResult({ success: false, source: "manual", message: errMsg });
      toast.error(typeof errMsg === "string" ? errMsg : "Manual batch submission failed.");
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first.");
      return;
    }

    setUploading(true);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append("file", prepared.payloadFile);
      if (prepared.sourceOriginalFileName) {
        formData.append("source_original_file_name", prepared.sourceOriginalFileName);
      }

      const response = await uploadSettlementPdfRegenerationBatch(formData);
      const result = response.data;
      setUploadResult({
        success: true,
        uploadId: result.upload_id,
        fileName: result.file_name,
        fileSize: result.file_size,
        message: result.message,
      });
      toast.success("File uploaded successfully. Processing started in background.");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message || "Upload failed.";
      setUploadResult({ success: false, message: errMsg });
      toast.error(typeof errMsg === "string" ? errMsg : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileSelect({ target: { files: e.dataTransfer.files } });
    }
  };

  const handleDownloadCsvTemplate = () => {
    const csvContent = TEMPLATE_HEADERS.map(csvEscape).join(",") + "\r\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "batch_regenerate_pis_form2307_template.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadExcelTemplate = async () => {
    const XLSX = await import("xlsx");
    const worksheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PRN");
    XLSX.writeFile(workbook, "batch_regenerate_pis_form2307_template.xlsx");
  };

  const handleOpenSpecs = async () => {
    setSpecsModalOpen(true);
    setSpecsError("");

    if (specsContent || specsLoading) {
      return;
    }

    setSpecsLoading(true);
    try {
      const response = await getSettlementPdfRegenerationBatchSpecs();
      setSpecsContent(response.data?.content || "");
      setSpecsMeta({
        fileName: response.data?.file_name || "batch-regenerate-pis-form2307-specs.md",
        updatedAt: response.data?.updated_at || "",
      });
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load specs.";
      setSpecsError(message);
      toast.error(message);
    } finally {
      setSpecsLoading(false);
    }
  };

  const manualResendAllChecked = manualRows.length > 0 && manualRows.every((row) => row.resend_email_notifications);
  const manualDuplicateCount = manualRows.length - new Set(manualRows.map((row) => row.payment_ref_no.toUpperCase())).size;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">Batch Regenerate PIS/Form 2307</h1>
          <p className="text-gray-600">Upload PRNs to regenerate PIS, PIS No PPD, and Form 2307 when available.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleOpenSpecs}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-blue-200 text-blue-700 bg-blue-50 rounded hover:bg-blue-100"
          >
            <FontAwesomeIcon icon={faFileAlt} />
            View Specs
          </button>
          <button
            onClick={() => navigate("/settlement-list")}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to Settlement List
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-2/5 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between mb-4 gap-3">
              <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
                File Format
              </h3>
              <div className="flex gap-2 flex-wrap justify-end">
                <button
                  onClick={handleDownloadCsvTemplate}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  CSV
                </button>
                <button
                  onClick={handleDownloadExcelTemplate}
                  className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Excel
                </button>
              </div>
            </div>

            <div className="space-y-2 mb-5">
              <div className="flex items-start gap-2 p-2.5 rounded-lg border bg-red-50 border-red-200">
                <div className="flex-shrink-0 mt-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-red-700">Payment Ref No</span>
                  <span className="ml-1.5 text-xs font-bold text-red-600 uppercase tracking-wide">*required</span>
                  <p className="text-xs text-gray-500 mt-0.5">Accepted aliases: PRN, Payment Reference No, Payment Reference Number</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-2.5 rounded-lg border bg-blue-50 border-blue-200">
                <div className="flex-shrink-0 mt-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-blue-700">Resend Email Notifications</span>
                  <span className="ml-1.5 text-xs font-bold text-blue-600 uppercase tracking-wide">optional</span>
                  <p className="text-xs text-gray-500 mt-0.5">Default: no. Only value yes will resend provider or physician email notifications.</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-bold text-blue-800 mb-1.5 flex items-center gap-1">
                <FontAwesomeIcon icon={faInfoCircle} /> Notes
              </p>
              <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                <li>Email notifications are not resent unless Resend Email Notifications is yes.</li>
                <li>Email resend is queued only when the matching settlement is Paid.</li>
                <li>Excel uploads are converted to a PRN CSV payload before upload.</li>
                <li>Maximum file size: 10 MB and maximum rows: 5,000.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="lg:flex-1">
          <div className="bg-white rounded-lg shadow-md p-6 mb-5">
            <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
              Manual PRN Entry
            </h3>

            <textarea
              value={manualPrnText}
              onChange={(event) => setManualPrnText(event.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono resize-y"
              placeholder={"P-000001-000001\nD-000002-000002\nP-000003-000003, D-000004-000004"}
              disabled={manualSubmitting}
            />

            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-gray-500">
                {parseManualPrnRows().length.toLocaleString()} PRN{parseManualPrnRows().length === 1 ? "" : "s"} detected
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleManualPreview}
                  disabled={manualSubmitting || manualPrnText.trim() === ""}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-lg"
                >
                  <FontAwesomeIcon icon={faEye} />
                  Preview
                </button>
                <button
                  onClick={() => {
                    setManualPrnText("");
                    setManualRows([]);
                  }}
                  disabled={manualSubmitting || manualPrnText.trim() === ""}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-5">
            <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faCloudUploadAlt} className="text-blue-600" />
              Upload PRN File
            </h3>

            <div
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                isDragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <FontAwesomeIcon
                icon={faCloudUploadAlt}
                className={`text-5xl mb-3 ${isDragOver ? "text-blue-600" : "text-blue-400"}`}
              />
              <h4 className="text-base font-semibold text-gray-700 mb-1">Drag and drop your file here</h4>
              <p className="text-sm text-gray-500 mb-3">or click to browse</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              <p className="text-xs text-gray-400">Accepted: .csv, .txt, .xlsx, .xls - Max size: 10 MB</p>
            </div>

            {file && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
                <button
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove file"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}

            {file && (
              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faUpload} className={uploading ? "animate-spin" : ""} />
                  {uploading ? "Uploading..." : "Upload & Process"}
                </button>
                <button
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  disabled={uploading}
                  className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Clear
                </button>
              </div>
            )}
          </div>

          {uploadResult && (
            <div className={`rounded-lg p-5 ${uploadResult.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={uploadResult.success ? faCheckCircle : faTimesCircle}
                  className={`text-2xl flex-shrink-0 mt-0.5 ${uploadResult.success ? "text-green-600" : "text-red-600"}`}
                />
                <div className="flex-1">
                  <h4 className={`text-base font-bold mb-1 ${uploadResult.success ? "text-green-800" : "text-red-800"}`}>
                    {uploadResult.success
                      ? (uploadResult.source === "manual" ? "Manual Batch Queued" : "Upload Successful")
                      : (uploadResult.source === "manual" ? "Manual Batch Failed" : "Upload Failed")}
                  </h4>
                  <p className={`text-sm ${uploadResult.success ? "text-green-700" : "text-red-700"}`}>
                    {typeof uploadResult.message === "string" ? uploadResult.message : "An error occurred."}
                  </p>

                  {uploadResult.success && uploadResult.uploadId && (
                    <>
                      <div className="mt-3 p-2.5 bg-white rounded border border-green-200">
                        <p className="text-xs text-gray-500 mb-0.5">Upload ID:</p>
                        <p className="font-mono text-xs text-gray-800 break-all">{uploadResult.uploadId}</p>
                      </div>

                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => navigate(`/batch-regenerate-pis-form2307-processed/${uploadResult.uploadId}`)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View Processed
                        </button>
                        <button
                          onClick={() => navigate("/batch-regenerate-pis-form2307-history")}
                          className="flex-1 bg-gray-700 hover:bg-gray-800 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faHistory} />
                          View History
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {manualPreviewOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Review Manual PRNs</h2>
                <p className="text-xs text-gray-500 mt-1">
                  {manualRows.length.toLocaleString()} row{manualRows.length === 1 ? "" : "s"} ready for regeneration
                </p>
              </div>
              <button
                onClick={() => setManualPreviewOpen(false)}
                disabled={manualSubmitting}
                className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-gray-100 text-gray-500 disabled:opacity-50"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              {manualDuplicateCount > 0 && (
                <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  {manualDuplicateCount.toLocaleString()} duplicate PRN{manualDuplicateCount === 1 ? "" : "s"} detected. Duplicate rows may fail during processing.
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-16">#</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Payment Ref No</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-64">
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={manualResendAllChecked}
                              onChange={(event) => handleManualSelectAllResend(event.target.checked)}
                              disabled={manualSubmitting || manualRows.length === 0}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            Resend Email Notifications
                          </label>
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 w-20">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {manualRows.map((row, index) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 break-all">{row.payment_ref_no}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={row.resend_email_notifications}
                                onChange={(event) => handleManualResendToggle(index, event.target.checked)}
                                disabled={manualSubmitting}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              Yes
                            </label>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => handleManualRemoveRow(index)}
                              disabled={manualSubmitting}
                              className="h-8 w-8 inline-flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                              title="Remove row"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-gray-500">
                {manualRows.filter((row) => row.resend_email_notifications).length.toLocaleString()} row{manualRows.filter((row) => row.resend_email_notifications).length === 1 ? "" : "s"} marked for email resend
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setManualPreviewOpen(false)}
                  disabled={manualSubmitting}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleManualSubmit}
                  disabled={manualSubmitting || manualRows.length === 0}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-semibold inline-flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faUpload} className={manualSubmitting ? "animate-spin" : ""} />
                  {manualSubmitting ? "Submitting..." : "Submit & Process"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {specsModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Batch Regenerate PIS/Form 2307 Specs</h2>
                <p className="text-xs text-gray-500 mt-1">
                  {specsMeta?.fileName || "batch-regenerate-pis-form2307-specs.md"}
                  {specsMeta?.updatedAt ? ` - Updated ${specsMeta.updatedAt}` : ""}
                </p>
              </div>
              <button
                onClick={() => setSpecsModalOpen(false)}
                className="h-9 w-9 inline-flex items-center justify-center rounded hover:bg-gray-100 text-gray-500"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              {specsLoading ? (
                <div className="py-12 text-center text-gray-500">Loading specs...</div>
              ) : specsError ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">{specsError}</div>
              ) : specsContent ? (
                <MarkdownSpecsContent content={specsContent} />
              ) : (
                <div className="py-12 text-center text-gray-500">No specs content available.</div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => setSpecsModalOpen(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default SettlementPdfRegenerationBatchUploadPage;
